import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MergedProduct } from 'src/app/core/models/product.model';
import { ProductsService } from 'src/app/core/services/products.service';
import { AppsService } from 'src/app/shared/services/apps.service';
import { TrackingService } from 'src/app/shared/services/tracking.service';

interface HeaderItem {
  title: string;
  background?: string;
  count?: number;
  hue: number;
  saturation: number;
  lightness: number;
}

interface ContentItem {
  title: string;
  filter: HeaderItem[];
  icon: string;
  packs: boolean[];
  video?: string;
}

@Component({
  selector: 'app-applications',
  templateUrl: './applications.component.html',
  styleUrls: ['./applications.component.scss'],
})
export class ApplicationsComponent implements OnInit {
  @ViewChild('headerContainer') headerContainer!: ElementRef;
  @ViewChild('cardsContainer') cardsContainer!: ElementRef;

  overlay!: HTMLElement;
  headers!: HeaderItem[];
  contents!: ContentItem[];
  content_packs!: MergedProduct[];
  selectedHeader!: HeaderItem;
  selectedIndex: number = 0;
  selectedWidth: string = '0px';
  selectedLeft: string = '0px';
  firstContentWidth: number = 0;
  firstContentHeight: number = 0;
  showAll = false;
  isMobile: boolean = false;
  private boundApplyOverlayMask!: (e: PointerEvent) => void;

  displayVideoModal = false;
  videoId: string | undefined;
  videoUrl: SafeResourceUrl = '';
  isLoading = true;
  trackingSent: boolean = false;
  entryTimer: any;

  constructor(
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private applicationsService: AppsService,
    private productsService: ProductsService,
    private trackingService: TrackingService
  ) {
    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      'https://www.youtube.com/embed/'
    );
  }

  ngOnInit(): void {
    this.headers = this.applicationsService.getFilters();

    this.contents = this.applicationsService.getApps();

    this.productsService.getProductsData().subscribe((products) => {
      this.content_packs = products;
    });

    this.selectedHeader = this.headers[0];

    this.shuffleArray(this.contents);

    this.headers.forEach((header) => {
      const matchingContents = this.contents.filter((content) =>
        content.filter.some((f) => f.title === header.title)
      );

      header.count = matchingContents.length;
    });
  }

  updateVideoUrl() {
    if (this.videoId) {
      const cleanedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        'https://www.youtube.com/embed/' + this.videoId
      );
      this.videoUrl = cleanedUrl;
    }
  }

  openVideoModal(videoId: string) {
    if (videoId === '') return;

    this.videoId = videoId;
    this.updateVideoUrl();
    this.isLoading = true;
    this.displayVideoModal = true;
  }

  onVideoLoad() {
    this.isLoading = false;
  }

  selectHeader(header: HeaderItem) {
    this.selectedHeader = header;
    this.showAll = false;
    this.selectedIndex = this.headers.indexOf(header);
    this.updateSelectedDimensions();
  }

  toggleContentDisplay() {
    this.showAll = !this.showAll;
  }

  ngAfterViewInit(): void {
    this.isMobile = window.innerWidth < 1200;
    this.boundApplyOverlayMask = this.applyOverlayMask.bind(this);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!this.entryTimer && !this.trackingSent) {
              this.entryTimer = setTimeout(() => {
                this.trackingService
                  .sendAppData({
                    event_name: 'SectionApp',
                  })
                  .subscribe();

                this.trackingSent = true;
                clearTimeout(this.entryTimer);
                this.entryTimer = null;
              }, 20000);
            }
          } else {
            if (this.entryTimer) {
              clearTimeout(this.entryTimer);
              this.entryTimer = null;
            }
          }

          if (entry.isIntersecting && !this.isMobile) {
            document.body.addEventListener(
              'pointermove',
              this.boundApplyOverlayMask
            );
          } else {
            document.body.removeEventListener(
              'pointermove',
              this.boundApplyOverlayMask
            );
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(this.cardsContainer.nativeElement);
  }

  applyOverlayMask(e: PointerEvent) {
    if (!this.cardsContainer) return;
    const cardsContainer = this.cardsContainer.nativeElement as HTMLElement;
    const overlay = this.cardsContainer.nativeElement.querySelector(
      '.overlay'
    ) as HTMLElement;

    if (!cardsContainer || !overlay) return;

    const x = e.pageX - cardsContainer.offsetLeft;
    const y = e.pageY - cardsContainer.offsetTop;
    overlay.style.setProperty('--opacity', '1');
    overlay.style.setProperty('--x', `${x}px`);
    overlay.style.setProperty('--y', `${y}px`);
  }

  ngOnDestroy(): void {
    document.body.removeEventListener(
      'pointermove',
      this.boundApplyOverlayMask
    );
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.isMobile = window.innerWidth < 1200;
  }

  calculateFirstContentDimensions() {
    const firstContent = document.querySelector(
      '.table_content .col-12'
    ) as HTMLElement;
    if (firstContent) {
      this.firstContentWidth = firstContent.offsetWidth;
      this.firstContentHeight = firstContent.offsetHeight;
    }
  }

  updateSelectedDimensions() {
    if (this.headerContainer) {
      const headers =
        this.headerContainer.nativeElement.querySelectorAll('.header');
      if (headers && headers.length > this.selectedIndex) {
        const selectedElement = headers[this.selectedIndex];
        this.selectedWidth = `${selectedElement.offsetWidth}px`;
        this.selectedLeft = `${selectedElement.offsetLeft}px`;
      }
    }
  }

  updateOverlayDimensions() {
    const overlayContainer = this.cardsContainer.nativeElement as HTMLElement;

    if (!overlayContainer || !this.overlay) return;

    this.overlay.style.setProperty('--width', `${this.firstContentWidth}px`);
    this.overlay.style.setProperty(
      '--height',
      `${this.firstContentHeight + 5}px`
    );
  }

  get displayedContents() {
    const filteredContents = this.contents.filter((content) =>
      content.filter.includes(this.selectedHeader)
    );

    return this.showAll ? filteredContents : filteredContents.slice(0, 8);
  }

  shuffleArray(array: any[]) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }
}
